import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Customer } from '../types';
import { SurveySummary } from '../types/survey';
import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const CustomerSelector: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [archivedCustomers, setArchivedCustomers] = useState<Customer[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [editName, setEditName] = useState('');
  const [editProjectName, setEditProjectName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Survey display state (read-only)
  const [customerSurveys, setCustomerSurveys] = useState<{ [customerId: string]: SurveySummary[] }>({});
  const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await apiClient.getCustomers();
      setCustomers(data);
      // Load surveys for each customer
      for (const customer of data) {
        loadCustomerSurveys(customer.customerId);
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerSurveys = async (customerId: string) => {
    try {
      const surveys = await apiClient.getCustomerSurveys(customerId);
      setCustomerSurveys(prev => ({ ...prev, [customerId]: surveys }));
    } catch (err) {
      console.error('Error loading customer surveys:', err);
    }
  };

  const loadArchivedCustomers = async () => {
    try {
      const data = await apiClient.getArchivedCustomers();
      setArchivedCustomers(data);
    } catch (err) {
      console.error('Error loading archived customers:', err);
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomerName.trim()) return;

    setCreating(true);
    setError('');

    try {
      const customer = await apiClient.createCustomer(newCustomerName, newCustomerEmail, newProjectName);
      setCustomers([customer, ...customers]);
      setNewCustomerName('');
      setNewProjectName('');
      setNewCustomerEmail('');
    } catch (err: any) {
      console.error('Error creating customer:', err);
      setError(err.response?.data?.error?.message || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCustomer = async (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation();
    if (!window.confirm(`「${customer.customerName}」${t('customer', 'deleteConfirm')}`)) {
      return;
    }
    try {
      await apiClient.deleteCustomer(customer.customerId);
      setCustomers(customers.filter(c => c.customerId !== customer.customerId));
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError(t('customer', 'deleteFailed'));
    }
  };

  const handleArchiveCustomer = async (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation();
    if (!window.confirm(`「${customer.customerName}」${t('customer', 'archiveConfirm')}`)) {
      return;
    }
    try {
      await apiClient.archiveCustomer(customer.customerId);
      setCustomers(customers.filter(c => c.customerId !== customer.customerId));
      if (showArchived) {
        loadArchivedCustomers();
      }
    } catch (err) {
      console.error('Error archiving customer:', err);
      setError(t('customer', 'archiveFailed'));
    }
  };

  const handleRestoreCustomer = async (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation();
    if (!window.confirm(`「${customer.customerName}」${t('customer', 'restoreConfirm')}`)) {
      return;
    }
    try {
      const restored = await apiClient.restoreCustomer(customer.customerId);
      setArchivedCustomers(archivedCustomers.filter(c => c.customerId !== customer.customerId));
      setCustomers([restored, ...customers]);
    } catch (err) {
      console.error('Error restoring customer:', err);
      setError(t('customer', 'restoreFailed'));
    }
  };

  const handleEditClick = (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation();
    setEditingCustomer(customer);
    setEditName(customer.customerName);
    setEditProjectName(customer.projectName || '');
    setEditEmail(customer.email || '');
  };

  const handleEditSave = async () => {
    if (!editingCustomer || !editName.trim()) return;
    setEditSaving(true);
    try {
      const updated = await apiClient.updateCustomer(editingCustomer.customerId, {
        customerName: editName.trim(),
        projectName: editProjectName.trim(),
        email: editEmail.trim(),
      });
      setCustomers(customers.map(c => c.customerId === updated.customerId ? updated : c));
      setEditingCustomer(null);
    } catch (err) {
      console.error('Error updating customer:', err);
      setError(t('customer', 'editFailed'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingCustomer(null);
    setEditName('');
    setEditProjectName('');
    setEditEmail('');
  };

  const handleToggleArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next) {
      loadArchivedCustomers();
    }
  };

  const handleToggleExpand = (e: React.MouseEvent, customerId: string) => {
    e.stopPropagation();
    setExpandedCustomer(expandedCustomer === customerId ? null : customerId);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return '配信中';
      case 'closed': return '終了';
      default: return '下書き';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'closed': return '#6c757d';
      default: return '#ffc107';
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <h2>{t('customer', 'title')}</h2>

      {/* Create new customer */}
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3>{t('customer', 'newCustomer')}</h3>
        <form onSubmit={handleCreateCustomer}>
          <div style={{ marginBottom: '10px' }}>
            <input
              type="text"
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
              placeholder={t('customer', 'customerNamePlaceholder')}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
              disabled={creating}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="プロジェクト名を入力"
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
              disabled={creating}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <input
              type="email"
              value={newCustomerEmail}
              onChange={(e) => setNewCustomerEmail(e.target.value)}
              placeholder={t('customer', 'emailPlaceholder')}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
              disabled={creating}
            />
          </div>
          {error && (
            <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>
          )}
          <button
            type="submit"
            disabled={creating || !newCustomerName.trim()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: creating ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {creating ? t('common', 'creating') : t('customer', 'createNew')}
          </button>
        </form>
      </div>

      {/* Existing customers */}
      <div>
        <h3>{t('customer', 'existingCustomers')}</h3>
        {customers.length === 0 ? (
          <p style={{ color: '#666' }}>{t('customer', 'noCustomers')}</p>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {customers.map((customer) => {
              const surveys = customerSurveys[customer.customerId] || [];
              const isExpanded = expandedCustomer === customer.customerId;
              return (
                <div
                  key={customer.customerId}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                >
                  {/* Customer Header */}
                  <div
                    style={{
                      padding: '16px 20px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fafafa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }}
                    onClick={(e) => handleToggleExpand(e, customer.customerId)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                          <span style={{ fontSize: '18px', fontWeight: 600, color: '#2c3e50' }}>
                            {customer.customerName}
                          </span>
                          {customer.projectName && (
                            <span style={{
                              fontSize: '14px',
                              color: '#7f8c8d',
                              padding: '2px 8px',
                              backgroundColor: '#ecf0f1',
                              borderRadius: '4px'
                            }}>
                              {customer.projectName}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', color: '#95a5a6' }}>
                          {customer.email && <span style={{ marginRight: '16px' }}>{customer.email}</span>}
                          {t('common', 'createdDate')} {new Date(customer.createdAt).toLocaleDateString('ja-JP')}
                        </div>
                        {/* Survey count badge */}
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            backgroundColor: surveys.length > 0 ? '#e8f5e9' : '#f5f5f5',
                            color: surveys.length > 0 ? '#2e7d32' : '#9e9e9e',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: 500
                          }}>
                            <span style={{ fontSize: '14px' }}>📋</span>
                            アンケート: {surveys.length}件
                          </span>
                          <span style={{
                            fontSize: '12px',
                            color: '#bdbdbd',
                            cursor: 'pointer'
                          }}>
                            {isExpanded ? '▲ 閉じる' : '▼ 詳細を見る'}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => handleEditClick(e, customer)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#3498db',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {t('customer', 'edit')}
                        </button>
                        <button
                          onClick={(e) => handleArchiveCustomer(e, customer)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#95a5a6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {t('customer', 'archive')}
                        </button>
                        <button
                          onClick={(e) => handleDeleteCustomer(e, customer)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#e74c3c',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {t('common', 'delete')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Survey List (Read-only) */}
                  {isExpanded && (
                    <div style={{
                      borderTop: '1px solid #ecf0f1',
                      padding: '16px 20px',
                      backgroundColor: '#fafafa'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#34495e', fontWeight: 600 }}>
                        紐付けされたアンケート一覧
                      </h4>

                      {surveys.length === 0 ? (
                        <div style={{
                          padding: '20px',
                          textAlign: 'center',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          border: '1px dashed #ddd'
                        }}>
                          <p style={{ color: '#bdbdbd', fontSize: '14px', margin: '0 0 8px 0' }}>
                            アンケートがまだありません
                          </p>
                          <p style={{ color: '#9e9e9e', fontSize: '12px', margin: 0 }}>
                            アンケート作成画面でこのお客様を選択するとここに表示されます
                          </p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {surveys.map((survey) => (
                            <div
                              key={survey.surveyId}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                backgroundColor: '#fff',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0',
                                transition: 'box-shadow 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                  <span style={{ fontWeight: 500, fontSize: '14px', color: '#2c3e50' }}>
                                    {survey.title}
                                  </span>
                                  <span style={{
                                    fontSize: '11px',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    backgroundColor: getStatusColor(survey.status),
                                    color: '#fff',
                                    fontWeight: 500
                                  }}>
                                    {getStatusLabel(survey.status)}
                                  </span>
                                </div>
                                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                                  回答: {survey.completedCount || 0}/{survey.respondentCount || 0}人
                                  {survey.createdAt && (
                                    <span style={{ marginLeft: '12px' }}>
                                      作成日: {new Date(survey.createdAt).toLocaleDateString('ja-JP')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                  onClick={() => navigate(`/admin/surveys/${survey.surveyId}`)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#3498db',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  詳細
                                </button>
                                <button
                                  onClick={() => navigate(`/admin/surveys/${survey.surveyId}/results`)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#27ae60',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  結果
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Hint to create survey */}
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 12px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#1565c0'
                      }}>
                        💡 新しいアンケートを追加するには、
                        <button
                          onClick={() => navigate('/admin/surveys/new')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#1565c0',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '12px',
                            padding: '0 4px'
                          }}
                        >
                          アンケート作成画面
                        </button>
                        でこのお客様を選択してください
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Archived customers toggle */}
      <div style={{ marginTop: '30px' }}>
        <button
          onClick={handleToggleArchived}
          style={{
            padding: '8px 16px',
            backgroundColor: 'transparent',
            color: '#6c757d',
            border: '1px solid #6c757d',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {showArchived ? t('customer', 'hideArchived') : t('customer', 'showArchived')}
        </button>

        {showArchived && (
          <div style={{ marginTop: '15px' }}>
            <h3>{t('customer', 'archivedCustomers')}</h3>
            {archivedCustomers.length === 0 ? (
              <p style={{ color: '#666' }}>{t('customer', 'noArchivedCustomers')}</p>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {archivedCustomers.map((customer) => (
                  <div
                    key={customer.customerId}
                    style={{
                      backgroundColor: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6',
                      opacity: 0.8,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px', color: '#495057' }}>
                          {customer.customerName}
                          {customer.projectName && (
                            <span style={{ fontWeight: 'normal', marginLeft: '8px', color: '#6c757d' }}>
                              / {customer.projectName}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6c757d' }}>
                          {customer.email && <span style={{ marginRight: '12px' }}>{customer.email}</span>}
                          {customer.archivedAt && (
                            <span>{t('customer', 'archivedDate')} {new Date(customer.archivedAt).toLocaleDateString('ja-JP')}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={(e) => handleRestoreCustomer(e, customer)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {t('customer', 'restore')}
                        </button>
                        <button
                          onClick={(e) => handleDeleteCustomer(e, customer)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {t('common', 'delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={handleEditCancel}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '24px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0' }}>{t('customer', 'editCustomer')}</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                お客様名
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                プロジェクト名
              </label>
              <input
                type="text"
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                メールアドレス
              </label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={handleEditCancel}
                style={{
                  padding: '8px 18px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {t('customer', 'editCancel')}
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving || !editName.trim()}
                style={{
                  padding: '8px 18px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: editName.trim() ? '#007bff' : '#adb5bd',
                  color: '#fff',
                  cursor: editSaving || !editName.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {editSaving ? '...' : t('customer', 'editSave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelector;
